import React, { useEffect } from "react";
import logo from "../img/enjuggernaut_logo.png";
import ResumeCard from "./ResumeCard";
import useWindowDimensions from "./WindowDimensions";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import { useNavigate } from "react-router-dom";
import { useAuthToken } from "../AuthProvider";
import { User, UserScore } from "../Profile";
import { TopBar } from "../Topbar";
import { Sidebar } from "../Sidebar";
import arrow_forward from "../img/arrow_forward_gray.svg";
import arrow_back from "../img/arrow_back_gray.svg";
import scoreIcon from "../img/analytics.svg";
import close from "../img/close.svg";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import info_mark from "../img/info_mark.svg";
import no_resumes from "../img/no_resumes.svg";
import add_document from "../img/add_document.svg";
import { Drawer } from "@mui/material";

export enum EntryStatus {
  PENDING = "PENDING",
  COMPLETE = "COMPLETE",
}

export enum EntrySource {
  USER = "USER",
  SYSTEM = "SYSTEM",
}

export interface ResumeEntry {
  file_name: string;
  date: string;
  score: number;
  possible_score: number;
  status: EntryStatus;
  source: EntrySource;
}

export interface ResumeDetails {
  contents: any;
  name: string;
  uuid: string;
}

function GetWidthDivisor(width: number) {
  if (width >= 1280) {
    return 6;
  }

  if (width >= 1024) {
    return 5;
  }

  if (width >= 768) {
    return 3;
  }

  return 2;
}

export function Account() {
  const [divisor, setDivisor] = React.useState<number>(1);
  const [isOpen, setIsOpen] = React.useState(false);
  const [numResumes, setNumResumes] = React.useState(0);
  const [resumeCredits, setResumeCredits] = React.useState(0);
  const [resumeDetails, setResumeDetails] = React.useState<ResumeDetails[]>([]);
  const [shownResumeDetails, setShownResumeDetails] = React.useState<
    ResumeDetails[]
  >([]);
  const [shownPage, setShownPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [splashtext, setSplashtext] = React.useState<string>("");
  const [numPending, setNumPending] = React.useState<number>(0);
  const [bannerResumeId, setBannerResumeId] = React.useState<string>("");
  const [bannerResumeName, setBannerResumeName] = React.useState<string>("");
  const [showBanner, setShowBanner] = React.useState<boolean>(false);
  const { isAuthenticated, token, isLoading, updateToken } = useAuthToken();
  const userInfo = useSelector((state: AppState) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const RESUMES_PER_PAGE = 10;

  const updateUserResume = (uuid: string) => {
    const updatedUser: User = {
      ...userInfo, // create a new object with all existing properties of the user object
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  const updateUserScore = (score: UserScore, uuid: string) => {
    const updatedUser: User = {
      ...userInfo, // create a new object with all existing properties of the user object
      score: score, // overwrite the name property with the new value
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function showScore(uuid: string) {
    const apiEndpoint =  "/api/score?file=" + uuid;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      if (response.ok) {
        const responseData = await response.json();
        const scores = responseData.message as UserScore;
        const uuid = responseData.uuid as string;
        updateUserScore(scores, uuid);
        navigate("/score?complete=1");
      } else {
        console.error("Error:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  async function claimResume() {
    if (!isAuthenticated && !isLoading) {
      updateRedirectUri("/resumes");
      navigate("/login");
    }

    const apiEndpoint =
       "/api/claim?file=" + userInfo.resume_uuid;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      // Make a POST request using fetch
      await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
      });

      updateUserResume("");
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured");
    }
  }

  const updateRedirectUri = (uri: string) => {
    dispatch({
      type: "UPDATE_URI",
      payload: uri,
    });
  };

  async function getResumes() {
    const apiEndpoint =  "/api/resumes";

    let headers = {};

    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      updateRedirectUri("/resumes");
      navigate("/login");
    } else {
      updateRedirectUri("");

      headers = {
        Authorization: `Bearer ${token}`,
      };

      if (userInfo.resume_uuid.length > 0) {
        claimResume();
      }

      try {
        setSplashtext("Loading...");

        // Make a POST request using fetch
        const response = await fetch(apiEndpoint, {
          method: "GET",
          headers: headers,
        });

        if (response.ok) {
          updateRedirectUri("");
          const responseData = await response.json();
          setResumeDetails(responseData.pdf_details);
          setResumeCredits(responseData.resume_credits);
          setShownPage(1);

          if (responseData.pdf_details.length > 0) {
            setSplashtext("");
          } else {
            setSplashtext("No resumes yet, add one to get started!");
          }
        } else {
          updateRedirectUri("/resumes");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
      }
    }
  }

  async function queryCompletedResumes() {
    const apiEndpoint =  "/api/completed_resumes";

    let headers = {};

    if (isLoading) {
      return;
    }

    if (!isAuthenticated) {
      updateRedirectUri("/resumes");
      navigate("/login");
    } else {
      updateRedirectUri("");

      headers = {
        Authorization: `Bearer ${token}`,
      };

      try {
        // Make a POST request using fetch
        const response = await fetch(apiEndpoint, {
          method: "GET",
          headers: headers,
        });

        if (response.ok) {
          updateRedirectUri("");
          const responseData = await response.json();

          if (responseData.completed_resumes.length > 0) {
            setBannerResumeId(responseData.completed_resumes[0].uuid);
            setBannerResumeName(responseData.completed_resumes[0].name);

            getResumes();
          }
        } else {
          updateRedirectUri("/resumes");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
      }
    }
  }

  const { height, width } = useWindowDimensions();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  React.useEffect(() => {
    if (!isLoading) {
      setDivisor(GetWidthDivisor(width));
    }
  }, [width, numResumes, isAuthenticated, isLoading]);

  React.useEffect(() => {
    getResumes();
  }, [numResumes, isAuthenticated, isLoading]);

  React.useEffect(() => {
    setShowBanner(bannerResumeId.length > 0 && bannerResumeName.length > 0);
  }, [bannerResumeId, bannerResumeName]);

  React.useEffect(() => {
    setNumResumes(resumeDetails.length);
    setTotalPages(Math.ceil(resumeDetails.length / RESUMES_PER_PAGE));

    if (resumeDetails.length == 0) {
      setTotalPages(1);
    }

    let pending = 0;

    resumeDetails.map((details, _) => {
      if (details.uuid == "blank.pdf") {
        pending += 1;
      }
    });

    setNumPending(pending);
  }, [resumeDetails]);

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (numPending > 0) {
        queryCompletedResumes();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [numPending]);

  React.useEffect(() => {
    if (resumeDetails.length <= RESUMES_PER_PAGE) {
      setShownResumeDetails(resumeDetails);
    } else {
      if (shownPage * RESUMES_PER_PAGE >= resumeDetails.length) {
        setShownResumeDetails(
          resumeDetails.slice((shownPage - 1) * RESUMES_PER_PAGE)
        );
      } else {
        setShownResumeDetails(
          resumeDetails.slice(
            (shownPage - 1) * RESUMES_PER_PAGE,
            shownPage * RESUMES_PER_PAGE
          )
        );
      }
    }
  }, [shownPage, totalPages, resumeDetails]);

  const decrementNumResumes = () => {
    setNumResumes(numResumes - 1);
  };

  if (isLoading) {
    return <div className="dark-panel"></div>;
  }

  return (
    <div>
      <header className="header dark-panel visible sticky top-0 z-20 mx-auto flex flex-row items-center justify-between p-2 lg:hidden lg:p-4">
        <img src={logo} className="h-auto w-1/2" alt="logo" />

        <div>
          <button
            onClick={toggleMenu}
            className="text-white hover:text-gray-300 focus:outline-none"
          >
            <svg
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="h-12 w-12"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </header>

      {showBanner && (
        <header className="header dark-panel sticky top-0 z-20 mx-auto flex w-full flex-row items-center justify-between p-4">
          <div>
            <h1 className="mx-4 mt-6 inline-block font-code text-base text-white md:text-lg lg:text-xl xl:text-2xl">
              <span className="text-orange inline-block font-bold">
                {bannerResumeName}
              </span>{" "}
              has completed processing!{" "}
              <button
                className="text-blue inline-block font-code font-bold text-white underline"
                onClick={() => showScore(bannerResumeId)}
              >
                Click here
              </button>{" "}
              or use the{" "}
              <img
                className="industry-img-selected inline-block pr-2"
                src={scoreIcon}
              />{" "}
              button to see your score and enter the{" "}
              <span className="text-blue font-bold">Resume Workshop</span>!
            </h1>
          </div>
          <button
            className="absolute right-0 top-0 ml-auto mr-6 mt-4"
            onClick={() => setShowBanner(false)}
          >
            <img className="industry-img" src={close} />
          </button>
        </header>
      )}

      <div className="flex min-h-screen max-w-full flex-row">
        <div className="light-panel flex w-full flex-col lg:w-4/5 lg:max-w-[80vw] xl:w-5/6 xl:max-w-[84vw]">
          <div>
            <div className="flex w-full flex-col justify-between md:flex-row">
              <h1 className="tooltip ml-4 mr-8 mt-8 line-clamp-1 flex text-right font-code text-lg font-bold  text-white lg:text-xl xl:text-2xl">
                <img src={info_mark} className={`mr-2 scale-75 lg:scale-100`} />
                Resume Credits: {resumeCredits}
              </h1>
              <Tooltip
                anchorSelect={`.tooltip`}
                className="font-code"
                place="bottom"
              >
                The number of resumes you can submit for analysis or generate
              </Tooltip>
              <div className="ml-auto mr-8 mt-8 flex flex-row">
                <button
                  className="mr-2"
                  onClick={() => {
                    if (shownPage > 1) {
                      setShownPage(shownPage - 1);
                    }
                  }}
                >
                  <img src={arrow_back} />
                </button>
                <h1 className="font-code font-bold text-[#CCCCC1]">
                  Page {shownPage} of {totalPages}
                </h1>
                <button
                  className="ml-2"
                  onClick={() => {
                    if (shownPage < totalPages) {
                      setShownPage(shownPage + 1);
                    }
                  }}
                >
                  <img src={arrow_forward} />
                </button>
              </div>
            </div>
            <div>
              <div className="px-8 py-8 lg:px-16 lg:py-16">
                <div className="grid grid-cols-1 gap-x-8 gap-y-16 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-36 lg:gap-y-24 xl:grid-cols-4">
                  {shownResumeDetails.map((details, index) => (
                    <div key={index}>
                      <ResumeCard
                        width={width / divisor}
                        data={details.contents}
                        filename={details.name}
                        uuid={details.uuid}
                        decrementFunc={decrementNumResumes}
                        showScore={showScore}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="w-full">
              {resumeDetails.length == 0 && (
                <img src={no_resumes} className="mx-auto px-8" />
              )}
              <h1 className="mx-auto mt-8 w-full text-center font-code text-lg font-bold text-[#828282] lg:text-xl xl:text-2xl">
                {splashtext}
              </h1>
            </div>
          </div>
          <button onClick={() => navigate("/start")}>
            <div className="2xl fixed bottom-5 right-5 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-[#ff8800] shadow-lg lg:right-[22vw] xl:right-[18vw]">
              <img src={add_document} />
            </div>
          </button>
        </div>
        <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)}>
          <Sidebar minimized={false} drawer={true} />
        </Drawer>
        <Sidebar minimized={false} drawer={false} />
      </div>
    </div>
  );
}
